import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Box, Alert, CircularProgress
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { schoolService, SchoolAdmin } from '../../services/schoolService';

interface Props {
  open: boolean;
  schoolId: string;
  admin: SchoolAdmin;
  onClose: () => void;
  onResetDone: () => void;
}

// Two-step pattern matching the rest of the portal: explicit confirmation
// inside a dedicated dialog so the destructive action is intentional.
const ResetAdminPasswordDialog: React.FC<Props> = ({ open, schoolId, admin, onClose, onResetDone }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const handleClose = () => {
    if (busy) return;
    setPassword(''); setConfirm('');
    onClose();
  };

  const handleSubmit = async () => {
    if (password.length < 6) return enqueueSnackbar('Password must be at least 6 characters', { variant: 'warning' });
    if (password !== confirm) return enqueueSnackbar('Passwords do not match', { variant: 'warning' });
    setBusy(true);
    try {
      await schoolService.resetAdminPassword(schoolId, admin._id, password);
      enqueueSnackbar('Password reset', { variant: 'success' });
      setPassword(''); setConfirm('');
      onResetDone();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to reset password', { variant: 'error' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Reset admin password</Typography>
        <Typography variant="caption" color="text.secondary">{admin.name} · {admin.email}</Typography>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5, mb: 2 }}>
          The admin's current password will be invalidated immediately. Share the new password with them through a secure channel.
        </Alert>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField label="New password" type="password" size="small" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} helperText="At least 6 characters" />
          <TextField label="Confirm password" type="password" size="small" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={busy} color="inherit">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="warning" disabled={busy} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined} sx={{ borderRadius: 1.5 }}>
          {busy ? 'Resetting…' : 'Reset password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetAdminPasswordDialog;
